import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { escapeHtml, PICKUP_FACET_FIELD } from '@theme/constructor-utils';
import { fetchPickupVocabulary, matchStoreRecord } from '@theme/pickup-store-vocabulary';
import { debounce, requestIdleCallback } from '@theme/utilities';
import {
  MIN_GEOCODE_LENGTH,
  SEARCH_DEBOUNCE_MS,
  geocodePlace,
  hasPostalPrefix,
  locateShopper,
  showRowsMatching,
  toPostalPrefix,
} from '@theme/pickup-search';
import {
  MILES_TO_KILOMETRES,
  PICKUP_RADIUS_MILES,
  PICKUP_STORE_CHANGED_EVENT,
  cleanStoreName,
  normalizeStoreName,
  readSelectedStore,
  readShopperLocation,
  setPickupEnabled,
  toCoordinate,
  writeSelectedStore,
  writeShopperLocation,
} from '@theme/pickup-store-storage';

const FILTER_PARAM = `filter.${PICKUP_FACET_FIELD}`;
const PAGE_PARAM = 'page';

/**
 * Filters products down to what the shopper can collect from one store.
 *
 * The list is Constructor's: the stores its pickup facet can filter on, which is the only
 * set every one of these rows is guaranteed to work for. Each is joined to its Uberall
 * record for the address, hours and coordinates it renders, sharing the store locator's
 * session cache.
 */
class CollectionPickupFilter extends Component {
  /** Both drawer paths render this, so the radio group is named per instance. */
  static #instances = 0;

  #instanceId = `CollectionPickupStore-${(CollectionPickupFilter.#instances += 1)}`;

  #controller = new AbortController();

  /** Memoized joined store list, so only the first drawer open pays for it. @type {Promise<any[]> | null} */
  #storesRequest = null;

  /** Locator helpers, imported on first finder open. @type {Promise<typeof import('./store-locator-helpers.js')> | null} */
  #helpers = null;

  /** Whether the list is limited to the pickup radius, which gates the "can't find a store
   * nearby" prompt. */
  #radius = false;

  /** Whether that radius is centred on the shopper, which switches the count wording. */
  #nearMe = false;

  /** Bumped per search, so a slow geocode can tell a later keystroke has overtaken it. */
  #searchToken = 0;

  /**
   * @type {Map<string, number> | null}
   */
  #pickupCounts = null;

  /**
   * The market the shopper is buying in, which scopes the stored store: these rows are the
   * stores collectable here, so a selection made in another market is not ours to read.
   */
  get #market() {
    return this.dataset.country;
  }

  /** The store currently applied as a filter, or '' when pickup is off. */
  get #activeStoreName() {
    return new URLSearchParams(window.location.search).get(FILTER_PARAM) ?? '';
  }

  /** Whether the pickup filter is on. */
  get #isActive() {
    return this.#activeStoreName !== '';
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    document.addEventListener('cnstrc:results-loaded', this.#handleResultsLoaded, { signal });
    // Catches the filter being dropped elsewhere: the chip, "clear all", a browser Back.
    document.addEventListener(ThemeEvents.FilterUpdate, this.#syncFromUrl, { signal });
    window.addEventListener('popstate', this.#syncFromUrl, { signal });
    // A store picked in the PDP drawer, or in the finder below.
    document.addEventListener(PICKUP_STORE_CHANGED_EVENT, this.#renderSelectedStore, { signal });

    this.#renderSelectedStore();
    this.#restorePickupState();
    this.#syncFromUrl();
    this.#chooseNearestStore();
  }

  /**
   * Picks the nearest store for a shopper who has none in this market, so the finder opens
   * already knowing where they collect from rather than asking them to find it.
   *
   * The PDP drawer does the same from its own rendered rows; here there are none until the
   * finder is opened, so this measures against the store list itself. That list is the one
   * Constructor's pickup facet can filter on, which is exactly the set this page could
   * choose from anyway.
   *
   * Deliberately kept cheap for everyone it doesn't apply to. A market that already has a
   * store returns before anything is fetched, so this costs one localStorage read on all but
   * a shopper's first collection page — and the store list, which the finder otherwise pays
   * for on first open, is only pulled forward for someone whose location we can read without
   * asking. Deferred to idle either way, so it never competes with the collection render.
   *
   * Only ever fills a gap, and only names a store: a choice the shopper made stands
   * untouched, and the pickup filter's `enabled` flag is left unset, so a store picked for
   * them never filters a collection down on its own. That stays something they turn on.
   */
  #chooseNearestStore() {
    if (readSelectedStore(this.#market)) return;

    requestIdleCallback(async () => {
      const coordinates = await locateShopper();
      if (!coordinates) return;

      // Re-checked: the finder, or a PDP drawer in a quick-add dialog on this page, may have
      // settled on a store while the location was resolving.
      if (readSelectedStore(this.#market)) return;

      try {
        const [helpers, stores] = await Promise.all([this.#loadHelpers(), this.#fetchStores()]);

        // Unverified Uberall locations carry no coordinates, so they can't be measured — the
        // same rows #renderStores drops from a located list.
        const located = stores.filter(
          (/** @type {any} */ store) =>
            Number.isFinite(Number(store?.lat)) && Number.isFinite(Number(store?.lng)),
        );
        const [nearest] = helpers.sortStoresByNearestOrigin(located, [coordinates]);

        // Beyond the pickup radius is beyond collecting from, and is how a shopper in a
        // market whose stores are all far away ends up with none picked rather than a
        // nominal nearest that is hours out.
        if (!nearest || !(Number(nearest.distance) <= PICKUP_RADIUS_MILES)) return;

        // Fires the change event, which is what puts the store's name under the toggle.
        writeSelectedStore(this.#market, {
          name: cleanStoreName(nearest.name),
          uberallId: nearest.uberallId,
          lat: toCoordinate(nearest.lat),
          lng: toCoordinate(nearest.lng),
        });
      } catch {
        // List or helpers unavailable: no store picked, and the toggle asks for one as before.
      }
    });
  }

  /**
   * Carries the toggle across page loads, since a new collection arrives with a fresh URL.
   * The stored name is Constructor's own facet value, so it needs nothing resolved against
   * a response that has not arrived yet.
   */
  #restorePickupState() {
    if (this.#isActive) return;

    const store = readSelectedStore(this.#market);
    if (!store?.enabled || !store.name) return;

    // Replaced, not pushed: restoring state isn't a navigation, and Back would be a no-op.
    this.#writeFilterUrl(store.name, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Turns pickup on or off, or opens the finder when no store is chosen yet. Whether the
   * store has anything to collect doesn't come into it: the toggle says where the shopper
   * wants to collect, and an empty result set is an answer to that, not a reason to refuse.
   */
  togglePickup = () => {
    if (this.#isActive) {
      this.#removeFilter();
      return;
    }

    const store = readSelectedStore(this.#market);
    if (!store?.name) {
      this.openFinder();
      return;
    }

    this.#applyFilter(store.name);
  };

  /** Opens the store finder. */
  openFinder = () => {
    const dialog = /** @type {(Element & { showDialog?: () => void }) | null} */ (
      this.querySelector('dialog-component')
    );
    dialog?.showDialog?.();

    void this.#renderStores();
  };

  /** @param {Event} event - The click event from a store row. */
  selectStore = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('a')) return;

    const radio = target.closest('[data-store-name]')?.querySelector('.pickup-location__radio');
    if (!(radio instanceof HTMLInputElement) || radio.checked) return;

    radio.checked = true;
  };

  /** Remembers the chosen store, filters to it and closes the finder. */
  confirmStore = () => {
    const radio = this.querySelector('.pickup-location__radio:checked');
    const row = radio instanceof HTMLInputElement ? radio.closest('[data-store-name]') : null;
    if (!(row instanceof HTMLElement)) return;

    const name = row.dataset.storeName ?? '';
    if (!name) return;

    writeSelectedStore(this.#market, {
      name,
      uberallId: row.dataset.uberallId,
      lat: toCoordinate(row.dataset.storeLat),
      lng: toCoordinate(row.dataset.storeLng),
    });

    this.#applyFilter(name);

    const dialog = /** @type {(Element & { closeDialog?: () => void }) | null} */ (
      this.querySelector('dialog-component')
    );
    dialog?.closeDialog?.();
  };

  /**
   * Debounced because a query naming no store has to be geocoded, and a request per
   * keystroke is both slow and billed.
   * @param {Event} event
   */
  filterStores = debounce(
    /** @param {Event} event */
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;

      void this.#search(input.value);
    },
    SEARCH_DEBOUNCE_MS,
  );

  /**
   * A postal code, whole or partial, is answered with the stores inside the pickup radius of
   * it; a name/street/city query shows those stores; anything else is geocoded to a radius.
   * Search shares the radius's `hidden`, so it replaces that view rather than intersecting it.
   * @param {string} query - What the shopper typed.
   */
  async #search(query) {
    const token = ++this.#searchToken;
    const normalized = query.trim().toLowerCase();

    if (!normalized) {
      this.clearSearch();
      return;
    }

    // Before the text match: a code is the front of a store's code, not a word in its
    // address. Google is no help with a partial one — it reads "1001" as 01001, Agawam MA.
    const prefix = toPostalPrefix(normalized);
    const anchors = prefix ? await this.#postalPrefixOrigins(prefix) : [];

    // A later keystroke owns the list now, and has already rendered its own answer.
    if (token !== this.#searchToken) return;

    if (anchors.length > 0) {
      await this.#renderStores(anchors, { fromShopper: false, keepSearch: true });
      return;
    }

    if (showRowsMatching(this.#rows, normalized) > 0) {
      this.#dropRadius();
      return;
    }

    // Only where no store's code matched. Each step runs only if the one before found
    // nothing, so the chain is sequential by nature.
    const coordinates =
      normalized.length >= MIN_GEOCODE_LENGTH
        ? await geocodePlace(query, this.dataset.mapsKey, this.dataset.country)
        : null;

    if (token !== this.#searchToken) return;

    // Every row is hidden already: the query matched no store and named no place.
    if (!coordinates) {
      this.#dropRadius();
      return;
    }

    // Rebuilt rather than filtered, so every row is labelled with its distance.
    await this.#renderStores([coordinates], { fromShopper: false, keepSearch: true });
  }

  /**
   * The points a postal search draws its radius around. Read from the Uberall list rather
   * than the rendered rows, since that list is what the render rebuilds from.
   * @param {string} prefix - A prefix from `toPostalPrefix`.
   * @returns {Promise<{lat: number, lng: number}[]>}
   */
  async #postalPrefixOrigins(prefix) {
    /** @type {{lat: number, lng: number}[]} */
    const origins = [];

    for (const store of await this.#fetchStores()) {
      if (!hasPostalPrefix(store?.zip, prefix)) continue;

      const lat = Number(store?.lat);
      const lng = Number(store?.lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) origins.push({ lat, lng });
    }

    return origins;
  }

  /**
   * Stops Enter submitting the filters form this drawer sits inside, which would reload the
   * page mid-search. The list already filters on every keystroke.
   * @param {Event} event - The keydown event from the store search field.
   */
  preventSearchSubmit = (event) => {
    if (/** @type {KeyboardEvent} */ (event).key === 'Enter') event.preventDefault();
  };

  /** Empties the search and drops the radius — the only way back out of a narrowed list. */
  clearSearch = () => {
    const input = this.querySelector('.pickup-location__search-input');
    if (input instanceof HTMLInputElement) input.value = '';

    // A pending or in-flight search would otherwise land on this list and narrow it again.
    this.#cancelSearch();

    for (const row of this.#rows) row.removeAttribute('hidden');

    this.#dropRadius();
  };

  /** Drops a debounced search, and disowns any geocode already on its way back. */
  #cancelSearch() {
    this.filterStores.cancel();
    this.#searchToken += 1;
  }

  /** Leaves the radius view and re-counts whatever the list is left showing. */
  #dropRadius() {
    this.#setRadius(false);
    this.#updateStoreCount();
  }

  /** Every store row currently rendered, in document order. */
  get #rows() {
    return this.querySelectorAll('[data-store-name]');
  }

  /**
   * Sorts nearest-first from the browser's geolocation and limits the list to the radius.
   * @param {Event} event - The click event from the location button.
   */
  useMyLocation = async (event) => {
    // Read synchronously: the delegated event is stale once this function awaits.
    const target = event.target;
    const button = target instanceof Element ? target.closest('button') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    button.disabled = true;
    // The list is about to be rebuilt around the shopper; a search still on its way back
    // would land on top of that.
    this.#cancelSearch();

    try {
      const { getUserGeolocation } = await this.#loadHelpers();
      const coordinates = await getUserGeolocation();

      writeShopperLocation(coordinates);

      await this.#renderStores(coordinates);
    } catch {
      // Denied, unsupported or timed out: the list keeps its alphabetical order.
    } finally {
      // Re-enabled even though #setRadius just hid it, for when a search brings it back.
      button.disabled = false;
    }
  };

  /** @param {Event} event - The `cnstrc:results-loaded` event. */
  #handleResultsLoaded = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail ?? {};
    const facets = detail.facets ?? [];
    const facet = facets.find(
      (/** @type {{field?: string}} */ entry) => entry?.field === PICKUP_FACET_FIELD,
    );

    this.#recordPickupCounts(facet, facets);
    this.#updateStockMessage(detail.totalResults);
  };

  /**
   * @param {{ values?: Array<{ value?: string, count?: number }> } | undefined} facet - The
   *   pickup facet, absent once no store has anything left.
   * @param {any[]} facets - Every facet in the response.
   */
  #recordPickupCounts(facet, facets) {
    if (!facets.length) return;

    /** @type {Map<string, number>} */
    const counts = new Map();

    for (const value of facet?.values ?? []) {
      const name = normalizeStoreName(value?.value);
      if (name) counts.set(name, Number(value?.count) || 0);
    }

    this.#pickupCounts = counts;
  }

  /**
   * @param {string} name - The store's name, as the drawer shows it.
   * @returns {boolean} True when the store has no results to switch to.
   */
  #hasNoStock(name) {
    if (!this.#pickupCounts) return false;
    return !this.#pickupCounts.get(normalizeStoreName(name));
  }

  /**
   * Notes when collecting from the shopper's store leaves them with nothing, offering them
   * another store. The filter itself stays on: the toggle is theirs to turn off, and an
   * empty grid at their store is a result they asked for.
   *
   * Keyed on an empty grid rather than the store's own facet count, which is the one thing
   * Constructor can't report here — it returns no facets at all for a result set of nothing,
   * so the count that would have said "no stock at this store" is exactly what goes missing.
   * @param {number | undefined} totalResults - Products in the current result set.
   */
  #updateStockMessage(totalResults) {
    // Nothing applied, so nothing to answer for — including when the chip, "clear all" or a
    // browser Back dropped the filter without passing through #removeFilter.
    if (!this.#isActive) {
      this.#toggleRecoveryMessage(true);
      return;
    }

    this.#toggleRecoveryMessage(totalResults !== 0);
  }

  /** Shows or hides the note (and its Change Store button) explaining the empty grid. */
  #toggleRecoveryMessage(hidden) {
    const message = this.querySelector('[data-pickup-recovery-message]');
    if (message instanceof HTMLElement) message.hidden = hidden;
  }

  /**
   * Applies the pickup filter for a store, leaving every other filter alone. The name is
   * Constructor's own facet value, so it is applied as it stands.
   * @param {string} name - The store's name, as the drawer shows it.
   */
  #applyFilter(name) {
    this.#toggleRecoveryMessage(true);
    // One store at a time: a shopper collects from somewhere, not from several places.
    this.#writeFilterUrl(name);
  }

  /** Drops the pickup filter, leaving every other filter alone. */
  #removeFilter() {
    this.#toggleRecoveryMessage(true);
    this.#writeFilterUrl('');
  }

  /**
   * Writes the pickup filter into the URL and asks the results list to refetch; an empty
   * value removes it. The dispatch is what syncs the switch — this component listens for its
   * own event alongside everyone else's.
   * @param {string} value - The facet value to filter on, or '' to drop the filter.
   * @param {boolean} [replace] - Replace the history entry instead of pushing one.
   */
  #writeFilterUrl(value, replace = false) {
    const url = new URL(window.location.href);

    if (value) url.searchParams.set(FILTER_PARAM, value);
    else url.searchParams.delete(FILTER_PARAM);
    url.searchParams.delete(PAGE_PARAM);

    if (replace) history.replaceState({}, '', url.toString());
    else history.pushState({}, '', url.toString());

    this.#dispatchFilterUpdate();
  }

  /** The detail carries every filter in the URL, matching what constructor-facets.js sends. */
  #dispatchFilterUpdate() {
    /** @type {string[]} */
    const filters = [];
    for (const [key, value] of new URLSearchParams(window.location.search)) {
      if (key.startsWith('filter.')) filters.push(`${key}=${encodeURIComponent(value)}`);
    }

    document.dispatchEvent(new CustomEvent(ThemeEvents.FilterUpdate, { detail: { filters } }));
  }

  /** Mirrors the URL onto the switch, and onto the state the next page reads. */
  #syncFromUrl = () => {
    const isActive = this.#isActive;

    const button = this.querySelector('[data-pickup-toggle]');
    if (button instanceof HTMLElement) {
      button.setAttribute('aria-pressed', String(isActive));
      button.setAttribute('aria-current', String(isActive));
    }

    this.#rememberPickupState(isActive);
  };

  /**
   * Keeps the stored flag in step with the URL. "Clear all", the chip's remove button and a
   * browser Back all drop the param without going through this component, and without this
   * the next page load would re-apply a filter the shopper had just cleared. Only the flag
   * moves — the store stays chosen.
   * @param {boolean} isActive - Whether the pickup filter is currently applied.
   */
  #rememberPickupState(isActive) {
    const store = readSelectedStore(this.#market);
    if (!store) return;

    // Guarded: this runs on every filter change, and each write also fires an event.
    if (store.enabled === isActive) return;

    setPickupEnabled(this.#market, isActive);
  }

  /** Names the chosen store beneath the toggle. Server-rendered hidden until there is one. */
  #renderSelectedStore = (event) => {
    const store =
      /** @type {CustomEvent} */ (event)?.detail?.store ?? readSelectedStore(this.#market);
    const row = this.querySelector('[data-selected-store-row]');
    const name = this.querySelector('[data-selected-store-name]');

    if (name instanceof HTMLElement && store?.name) name.textContent = store.name;
    if (row instanceof HTMLElement) row.hidden = !store?.name;
  };

  /**
   * Renders the store list, fetching it on first open. Nearest-first and limited to the
   * pickup radius once a location is known, alphabetical and complete until then.
   * @param {{lat: number, lng: number} | {lat: number, lng: number}[] | null} [origin] - The
   *   point(s) to measure from.
   * @param {object} [options]
   * @param {boolean} [options.fromShopper] - Whether that point is the shopper's own
   *   location, which is what the "near you" count and hidden location button describe.
   * @param {boolean} [options.keepSearch] - Leave the search box alone, for a render that is
   *   itself the answer to what's in it.
   */
  async #renderStores(
    origin = readShopperLocation(),
    { fromShopper = true, keepSearch = false } = {},
  ) {
    const list = this.querySelector('.pickup-location__list');
    if (!(list instanceof HTMLElement)) return;

    // Several points when a partial postal code anchors on more than one store.
    const points = (Array.isArray(origin) ? origin : [origin]).filter(Boolean);
    /** @type {{lat: number, lng: number}[] | null} */
    const origins = points.length > 0 ? points : null;

    // Rows are rebuilt below, so a leftover query would claim to filter a list it no longer does.
    const search = this.querySelector('.pickup-location__search-input');
    if (!keepSearch && search instanceof HTMLInputElement) search.value = '';

    // Only on the open with no rows yet: emptying a populated list would flash, not wait.
    const isFirstRender = !list.querySelector('[data-store-name]');
    this.#toggleSkeleton(list, isFirstRender);

    const [helpers, stores] = await Promise.all([this.#loadHelpers(), this.#fetchStores()]);

    this.#toggleSkeleton(list, false);

    // Left to say the one thing the skeleton can't: that there is nothing to show.
    const status = this.querySelector('[data-store-status]');
    if (status instanceof HTMLElement) {
      status.hidden = stores.length > 0;
      status.textContent = stores.length ? '' : (this.dataset.tNoStores ?? '');
    }

    // Unverified Uberall locations have no coordinates: they can't be shown to be in range.
    /** @type {any[]} */
    const located = [];
    /** @type {any[]} */
    const unlocated = [];
    for (const store of stores) {
      const hasCoordinates =
        Number.isFinite(Number(store?.lat)) && Number.isFinite(Number(store?.lng));
      (hasCoordinates ? located : unlocated).push(store);
    }

    const ordered = origins
      ? [...helpers.sortStoresByNearestOrigin(located, origins), ...unlocated]
      : helpers.sortStoresAlphabetically(stores);

    // Read once for the whole render rather than per row: each is a dataset read plus, for
    // the day names, a split and a fresh array.
    const strings = {
      days: this.#dayNames,
      hours: this.#hoursTranslations,
      storeDetails: this.dataset.tStoreDetails ?? '',
      storeHours: this.dataset.tStoreHours ?? '',
      distance: this.dataset.tDistance ?? '',
      unit: this.dataset.distanceUnit,
      unavailable: this.dataset.tUnavailable ?? '',
    };

    // Facet counts describe the current result set, so they only speak for every store while
    // the pickup filter is off — with it applied, every other store's count is 0 by definition
    // and the whole list would claim to be unavailable.
    const countsApply = Boolean(this.#pickupCounts) && !this.#isActive;

    const fragment = document
      .createRange()
      .createContextualFragment(
        ordered
          .map((store) => this.#renderStore(store, helpers, strings, origins, countsApply))
          .join(''),
      );
    // Built into the fragment, before it is in the document: appending to 52 live rows would
    // be 52 mutation records for the base Component's observer, each re-scanning for refs.
    this.#addPickupNotes(fragment);

    // Rows are direct children of the list, which is a flex column: nesting them in a
    // wrapper would put them a level below the `gap` that separates them on the PDP. So
    // they're swapped in place, ahead of the "view all" prompt that closes the list.
    for (const row of this.#rows) row.remove();
    list.insertBefore(fragment, this.querySelector('[data-view-all-locations]'));

    this.#setRadius(Boolean(origins), fromShopper);
    this.#restoreSelection();
    this.#updateStoreCount();
  }

  /**
   * Shows or hides the placeholder rows the drawer waits behind.
   * @param {HTMLElement} list
   * @param {boolean} loading
   */
  #toggleSkeleton(list, loading) {
    const skeleton = this.querySelector('[data-store-skeleton]');
    if (skeleton instanceof HTMLElement) skeleton.hidden = !loading;

    // Spelled out: `aria-busy` is a token attribute and '' is not one of its tokens.
    list.setAttribute('aria-busy', String(loading));
  }

  /**
   * Clones the checkout note into every row that has something to collect. It comes from a
   * Liquid template so the tooltip inside it stays owned by info-tooltip.liquid — a custom
   * element whose refs and popover would otherwise be hand-copied here. Which row shows it is
   * left to CSS, as on the PDP — which likewise only renders it beside "Available for pick up".
   * @param {DocumentFragment} root - The rows, before they are in the document.
   */
  #addPickupNotes(root) {
    const template = this.querySelector('[data-pickup-info-template]');
    if (!(template instanceof HTMLTemplateElement)) return;

    const rows = '[data-store-name]:not([data-store-unavailable])';
    for (const wrapper of root.querySelectorAll(`${rows} .pickup-location__availability-wrapper`)) {
      wrapper.append(template.content.cloneNode(true));
    }
  }

  /**
   * One store's row, mirroring the PDP drawer's markup. The one difference is the line the
   * PDP gives to "Available for pick up": with no variant to report on, it carries the
   * store's opening status instead, as the store locator's cards do — unless the pickup facet
   * puts the store at zero, in which case it carries the PDP's "Unavailable for pick up".
   *
   * @param {any} store - The Uberall location.
   * @param {typeof import('./store-locator-helpers.js')} helpers - The locator helpers.
   * @param {Record<string, any>} strings - Localized strings, read once for the whole render.
   * @param {{lat: number, lng: number}[] | null} origins - Points the list is measured from,
   *   when it is limited to the pickup radius at all.
   * @param {boolean} countsApply - Whether the facet counts speak for every store.
   * @returns {string} The row's markup.
   */
  #renderStore(store, helpers, strings, origins, countsApply) {
    const name = cleanStoreName(store.name);
    const address = [store.streetAndNumber, store.city, store.province].filter(Boolean).join(', ');
    // No postal code: a store's own is the only one a text match can find. Codes go through
    // #search's radius paths instead.
    const searchText = `${name} ${address}`.toLowerCase().trim();
    const id = `${this.#instanceId}-${store.key}`;
    const distance = Number(store.distance);
    const hasDistance = Number.isFinite(distance);
    const status = helpers.getStoreHoursStatus(store, strings.hours);
    // A store the shopper has already chosen is filtered on distance like any other: a list
    // headed "stores near you" can't hold one that's hours away. The line beneath the toggle
    // still names their choice, and "view all stores" brings the row back.
    const outOfRange = Boolean(origins) && !(hasDistance && distance <= PICKUP_RADIUS_MILES);

    // Nothing to collect here under the filters in play, so the opening status is beside the
    // point: the row says so in the PDP's words rather than reporting the hours of a store
    // the shopper can't use. A store with no facet value at all counts as zero too — it
    // doesn't stock this collection either way.
    const unavailable = countsApply && this.#hasNoStock(name);
    const availability = unavailable
      ? { modifier: 'unavailable', text: strings.unavailable }
      : { modifier: status.isOpen ? 'available' : 'unavailable', text: status.text };

    return `
      <div
        class="pickup-location__wrapper"
        data-store-name="${escapeHtml(name)}"
        data-store-search-text="${escapeHtml(searchText)}"
        data-uberall-id="${escapeHtml(store.uberallId)}"
        data-store-lat="${escapeHtml(store.lat)}"
        data-store-lng="${escapeHtml(store.lng)}"
        ${hasDistance ? `data-store-distance="${escapeHtml(distance)}"` : ''}
        ${unavailable ? 'data-store-unavailable' : ''}
        ${outOfRange ? 'hidden' : ''}
        on:click="collection-pickup-filter/selectStore"
      >
        <input
          class="pickup-location__radio"
          type="radio"
          id="${escapeHtml(id)}"
          name="${escapeHtml(this.#instanceId)}"
          value="${escapeHtml(name)}"
        />
        <label class="pickup-location__text-bold" for="${escapeHtml(id)}">
          ${escapeHtml(name)}
          ${hasDistance ? `<span class="pickup-location__distance">${escapeHtml(this.#formatDistance(distance, strings))}</span>` : ''}
        </label>
        <div class="pickup-location__address-wrapper">
          <address class="pickup-location__address">${escapeHtml(address)}</address>
          <span class="pickup-location__availability-wrapper">
            <p class="pickup-location__availability-text">
              <span class="pickup-location__availability-icon pickup-location__availability-icon--${availability.modifier}"></span>
              ${escapeHtml(availability.text)}
            </p>
          </span>
        </div>
        ${this.#renderStoreDetails(store, helpers, strings)}
      </div>
    `;
  }

  /**
   * The store details accordion, matching the PDP drawer's markup. Needs no lazy fetch: the
   * Uberall list already carries hours and phone.
   * @param {any} store - The Uberall location.
   * @param {typeof import('./store-locator-helpers.js')} helpers - The locator helpers.
   * @param {Record<string, any>} strings - Localized strings, read once for the whole render.
   * @returns {string} The accordion's markup, or '' when there's nothing to put in it.
   */
  #renderStoreDetails(store, helpers, strings) {
    const hours = helpers.formatOpeningHours(store.openingHours, strings.days);
    if (!hours.length && !store.phone) return '';

    return `
      <details class="store-locator__accordion pickup-location__accordion">
        <summary class="store-locator__accordion-header">
          <span>${escapeHtml(strings.storeDetails)}</span>
          <span class="store-locator__accordion-icon">
            <svg class="icon-plus" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M6 1v10M1 6h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <svg class="icon-minus" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M1 6h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </span>
        </summary>
        <div class="store-locator__accordion-content">
          ${
            store.phone
              ? `<p class="pickup-location__phone"><a href="tel:${escapeHtml(helpers.formatPhoneHref(store.phone))}">${escapeHtml(store.phone)}</a></p>`
              : ''
          }
          ${
            hours.length
              ? `<span>${escapeHtml(strings.storeHours)}</span>
                 <div class="store-locator__hours">
                   ${hours.map((line) => `<p>${escapeHtml(line)}</p>`).join('')}
                 </div>`
              : ''
          }
        </div>
      </details>
    `;
  }

  /**
   * A distance in the unit Liquid picked for this market; the helper works in miles.
   * @param {number} miles - Distance in miles.
   * @param {Record<string, any>} strings - Localized strings, read once for the whole render.
   * @returns {string} The formatted distance.
   */
  #formatDistance(miles, strings) {
    const distance = strings.unit === 'km' ? miles * MILES_TO_KILOMETRES : miles;

    // One decimal, matching the helper's own rounding of the miles value.
    return strings.distance.replace('{{ distance }}', distance.toFixed(1));
  }

  /**
   * Re-checks the chosen store's row, which the fresh markup knows nothing about. Matched on
   * the Uberall id where the selection carries one, and on the name otherwise, for a
   * selection stored before ids were recorded.
   */
  #restoreSelection() {
    const store = readSelectedStore(this.#market);
    const uberallId = store?.uberallId;
    const selected = normalizeStoreName(store?.name);
    if (!uberallId && !selected) return;

    for (const row of this.querySelectorAll('[data-store-name]')) {
      if (!(row instanceof HTMLElement)) continue;

      const matches = uberallId
        ? row.dataset.uberallId === uberallId
        : normalizeStoreName(row.dataset.storeName) === selected;
      if (!matches) continue;

      const radio = row.querySelector('.pickup-location__radio');
      if (radio instanceof HTMLInputElement) radio.checked = true;

      this.#correctStoredName(store, row);

      // Before the first row, not the top of the list — that would beat the count line.
      const list = row.parentElement;
      const firstRow = list?.querySelector('[data-store-name]');
      if (list && firstRow !== row) list.insertBefore(row, firstRow);
      return;
    }
  }

  /**
   * Rewrites a stored store under the name Constructor knows it by, where it was saved under
   * another one. Selections made before this list came from Constructor hold Uberall's
   * spelling — "Fashion Island" for the store its facet calls "Newport" — which no longer
   * filters to anything. The row was found by Uberall id, so this renames the same store
   * rather than choosing a different one.
   * @param {import('./pickup-store-storage.js').SelectedStore} store - The stored store.
   * @param {HTMLElement} row - Its row in this list.
   */
  #correctStoredName(store, row) {
    const name = row.dataset.storeName;
    if (!name || normalizeStoreName(store.name) === normalizeStoreName(name)) return;

    const corrected = writeSelectedStore(this.#market, { ...store, name });

    // The filter in the URL still spells it the old way, so it is matching nothing.
    if (corrected?.enabled && this.#isActive) this.#applyFilter(name);
  }

  /**
   * Hides the location button once the list already reflects where the shopper is.
   * @param {boolean} radius
   * @param {boolean} [fromShopper]
   */
  #setRadius(radius, fromShopper = false) {
    this.#radius = radius;
    this.#nearMe = radius && fromShopper;

    const button = this.querySelector('[data-use-my-location]');
    if (button instanceof HTMLElement) button.hidden = this.#nearMe;
  }

  /** Rewrites the count, and shows the "view all" prompt when rows are held back. */
  #updateStoreCount() {
    const total = this.#rows.length;
    const visible = this.querySelectorAll('[data-store-name]:not([hidden])').length;

    // Radius view only: it answers "can't find a store nearby?", which the full listing
    // that pressing it produces has not asked.
    const viewAll = this.querySelector('[data-view-all-locations]');
    if (viewAll instanceof HTMLElement) viewAll.hidden = !this.#radius || visible === total;

    const target = this.querySelector('[data-store-count]');
    if (!(target instanceof HTMLElement)) return;

    const one = this.#nearMe ? this.dataset.countNearOne : this.dataset.countOne;
    const other = this.#nearMe ? this.dataset.countNearOther : this.dataset.countOther;
    const template = (visible === 1 ? one : other) ?? '';
    if (template) target.textContent = template.replace('{{ count }}', String(visible));
  }

  /**
   * The strings getStoreHoursStatus interpolates, in the shape it expects.
   * @returns {Record<string, string>} The opening-status strings.
   */
  get #hoursTranslations() {
    return {
      open_now: this.dataset.tOpenNow ?? '',
      open_until: this.dataset.tOpenUntil ?? '',
      opens_at: this.dataset.tOpensAt ?? '',
      closed: this.dataset.tClosed ?? '',
      temporarily_closed: this.dataset.tTemporarilyClosed ?? '',
      coming_soon: this.dataset.tComingSoon ?? '',
    };
  }

  /**
   * Day names for the hours formatter. Index 0 is empty so the array lines up with Uberall's
   * dayOfWeek (1 = Monday).
   * @returns {string[] | undefined} Day names, or undefined to use the formatter's default.
   */
  get #dayNames() {
    const days = this.dataset.tDays;

    return days ? ['', ...days.split(',')] : undefined;
  }

  /**
   * Loads the store locator helpers on first use, memoized.
   * @returns {Promise<typeof import('./store-locator-helpers.js')>} The helpers module.
   */
  #loadHelpers() {
    this.#helpers ??= import('@theme/store-locator-helpers');

    return this.#helpers;
  }

  /**
   * The stores this market can collect from: Constructor's list, each joined to its Uberall
   * record for the address, hours and coordinates a row renders. A store Constructor cannot
   * filter on never reaches the drawer, so every store on offer produces a working filter.
   * @returns {Promise<any[]>} The stores, or an empty array when there are none.
   */
  #fetchStores() {
    this.#storesRequest ??= this.#loadStores().catch(() => {
      // Drop the memo so the next open can retry.
      this.#storesRequest = null;
      return [];
    });

    return this.#storesRequest;
  }

  /**
   * Joins the two lists. Constructor's spelling is kept whatever the record says, since it
   * is what the filter is applied with; a store with no record still lists, just without an
   * address or hours to show.
   * @returns {Promise<any[]>}
   */
  async #loadStores() {
    const [vocabulary, locations] = await Promise.all([
      fetchPickupVocabulary(),
      this.#fetchLocations(),
    ]);

    return vocabulary.map((value) => {
      const record = matchStoreRecord(
        value,
        locations,
        (location) => /** @type {any} */ (location)?.name,
        (location) => /** @type {any} */ (location)?.city,
      );

      return {
        ...record,
        name: value,
        uberallId: /** @type {any} */ (record)?.id ?? '',
        // Only ever a DOM id and a render key. Falls back to the value so an unmatched
        // store still gets a unique one.
        key: /** @type {any} */ (record)?.id ?? value,
      };
    });
  }

  /**
   * Fetches the Uberall store list, sharing the store locator's session cache.
   * @returns {Promise<any[]>} The Uberall locations, or an empty array on failure.
   */
  #fetchLocations() {
    const storeKey = this.dataset.apiKey;
    if (!storeKey) return Promise.resolve([]);

    return this.#loadHelpers()
      .then(async (helpers) => {
        // The cache helper is untyped (`Object | null`); it stores the locations array.
        const cached = /** @type {any[] | null} */ (helpers.getCachedStores());
        if (cached) return cached;

        // Same field set the locator caches, so neither can serve the other a short payload.
        const url = `${helpers.getApiEndpoint(storeKey)}${helpers.buildFieldMask(helpers.STORE_LIST_FIELDS)}`;
        const response = await fetch(url, { signal: this.#controller.signal });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const locations = (await response.json()).response?.locations ?? [];
        helpers.setCachedStores(locations);

        return locations;
      })
      .catch(() => []);
  }
}

if (!customElements.get('collection-pickup-filter')) {
  customElements.define('collection-pickup-filter', CollectionPickupFilter);
}

export { CollectionPickupFilter };
